import { Chess } from 'chess.js'
import type { AdminPuzzleInput, PuzzleMistake, PuzzleNode } from '@chessx/shared/types'
import {
  PUZZLE_SETUP_FALLBACK_TEXT,
  getMistakeFeedback,
  getPuzzleTitle,
  getSolutionFeedback,
  getSolutionPath,
  getVoiceText,
  getYourMoveText
} from '@chessx/shared/puzzle'

export type WalkPhase = 'intro' | 'yourMove' | 'wrong' | 'correct' | 'solved'

export type WalkMood = 'coach' | 'prompt' | 'wrong' | 'correct'

export interface WalkState {
  phase: WalkPhase
  fen: string
  lastMove: string | null
  text: string
  node: PuzzleNode
  yourMoveFen: string
  yourMoveText: string
  issues: string[]
}

export const WALK_MOODS: Record<WalkPhase, WalkMood> = {
  intro: 'coach',
  yourMove: 'prompt',
  wrong: 'wrong',
  correct: 'correct',
  solved: 'correct'
}

interface MoveResult {
  fen: string
  issue: string | null
}

const applyUci = (fen: string, uci: string | undefined, label: string): MoveResult => {
  if (!uci) return { fen, issue: `${label} has no uci` }
  try {
    const chess = new Chess(fen)
    chess.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] })
    return { fen: chess.fen(), issue: null }
  } catch {
    return { fen, issue: `${label} ${uci} is not legal in ${fen}` }
  }
}

const withIssues = (issues: string[], ...next: Array<string | null>): string[] => [
  ...issues,
  ...next.filter((issue): issue is string => Boolean(issue))
]

export const startWalk = (puzzle: AdminPuzzleInput): WalkState => ({
  phase: 'intro',
  fen: puzzle.opponentMove.fenBefore,
  lastMove: null,
  text: getVoiceText(puzzle.opponentMove.intro) ?? PUZZLE_SETUP_FALLBACK_TEXT,
  node: puzzle.root,
  yourMoveFen: puzzle.root.fen,
  yourMoveText: '',
  issues: []
})

const enterNode = (
  state: WalkState,
  puzzle: AdminPuzzleInput,
  node: PuzzleNode,
  fen: string,
  lastMove: string | null,
  comment: string | undefined,
  issues: string[]
): WalkState => {
  const text = getYourMoveText(puzzle, node, comment)
  return {
    ...state,
    phase: 'yourMove',
    fen,
    lastMove,
    text,
    node,
    yourMoveFen: fen,
    yourMoveText: text,
    issues
  }
}

export const playOpponentMove = (puzzle: AdminPuzzleInput): WalkState => {
  const intro = startWalk(puzzle)
  const { opponentMove } = puzzle
  const played = applyUci(opponentMove.fenBefore, opponentMove.uci, 'opponentMove')
  const fen = played.issue ? puzzle.root.fen : played.fen
  return enterNode(
    intro,
    puzzle,
    puzzle.root,
    fen,
    played.issue ? null : opponentMove.uci,
    getVoiceText(opponentMove.comment),
    withIssues([], played.issue)
  )
}

const backToYourMove = (state: WalkState): WalkState =>
  state.phase === 'wrong'
    ? {
        ...state,
        phase: 'yourMove',
        fen: state.yourMoveFen,
        text: state.yourMoveText,
        lastMove: null
      }
    : state

const continueToNextNode = (state: WalkState, puzzle: AdminPuzzleInput): WalkState => {
  const next = state.node.solution.next
  if (!next) return state
  const reply = state.node.solution.reply
  if (!reply) return enterNode(state, puzzle, next, next.fen, null, undefined, state.issues)
  const played = applyUci(state.fen, reply.uci, 'solution.reply')
  return enterNode(
    state,
    puzzle,
    next,
    played.issue ? next.fen : played.fen,
    played.issue ? null : reply.uci,
    getVoiceText(reply.comment),
    withIssues(state.issues, played.issue)
  )
}

export const playCorrectMove = (state: WalkState, puzzle: AdminPuzzleInput): WalkState => {
  if (state.phase === 'correct') return continueToNextNode(state, puzzle)
  if (state.phase !== 'yourMove' && state.phase !== 'wrong') return state
  const from = backToYourMove(state)
  const { solution } = from.node
  const played = applyUci(from.fen, solution.uci, 'solution')
  const moved = {
    ...from,
    fen: played.fen,
    lastMove: played.issue ? null : solution.uci,
    issues: withIssues(from.issues, played.issue)
  }
  if (!solution.next) {
    const gems = puzzle.gemReward === undefined ? '' : ` · +${puzzle.gemReward} gems`
    return { ...moved, phase: 'solved', text: `Solved! ${getPuzzleTitle(puzzle)}${gems}` }
  }
  return { ...moved, phase: 'correct', text: getSolutionFeedback(from.node) }
}

export const playWrongMove = (
  state: WalkState,
  puzzle: AdminPuzzleInput,
  mistake: PuzzleMistake | undefined
): WalkState => {
  if (state.phase !== 'yourMove' && state.phase !== 'wrong') return state
  const from = backToYourMove(state)
  const text = getMistakeFeedback(puzzle, mistake)
  if (!mistake) return { ...from, phase: 'wrong', text, lastMove: null }
  const played = applyUci(from.fen, mistake.uci, `mistake ${mistake.san}`)
  const refuted = mistake.refutation
    ? applyUci(played.fen, mistake.refutation.uci, `refutation of ${mistake.san}`)
    : { fen: played.fen, issue: null }
  return {
    ...from,
    phase: 'wrong',
    text,
    fen: refuted.fen,
    lastMove: mistake.refutation && !refuted.issue ? mistake.refutation.uci : mistake.uci,
    issues: withIssues(from.issues, played.issue, refuted.issue)
  }
}

export interface WalkProgress {
  index: number
  count: number
}

export const getWalkProgress = (state: WalkState, puzzle: AdminPuzzleInput): WalkProgress => {
  const path = getSolutionPath(puzzle.root)
  return { index: Math.max(0, path.indexOf(state.node)), count: path.length }
}
